import axios from "axios";
import { useState } from "react";
import { fetchCurrentProfile } from "../../api/profile";

import {
  Button,
  CircularProgress,
  CssBaseline,
  Snackbar,
  SvgIcon,
  ThemeProvider,
} from "@mui/material";

import MuiAlert from "@mui/material/Alert";

import { usePlaidLink } from "react-plaid-link";
import { muiTheme } from "../welcomeSetup/theme/theme";
import { FakePlaidDialog } from "./FakePlaidDialog";

export const LinkPlaidButton = ({
  customer_id,
  refreshList,
  plaiditem_id,
  missing = false,
  product = "transactions",
  setProfile = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [publicToken, setPublicToken] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showFake, setShowFake] = useState(false);

  const formatError = (err) => {
    const data = err?.response?.data;

    if (typeof data === "string") return data;
    if (data?.detail) return data.detail;

    // If backend includes Plaid payload, try to show a meaningful message.
    const plaid = data?.plaid;
    if (plaid?.error_message) return plaid.error_message;
    if (plaid?.display_message) return plaid.display_message;
    if (plaid?.error_code || plaid?.error_type) {
      return `${plaid.error_type || "PLAID_ERROR"}: ${plaid.error_code || ""}`.trim();
    }

    return err?.message || "Failed to link bank account.";
  };

  const openPlaidLink = async () => {
    if (loading) return;
    setLoading(true);
    const params = { product, ...(plaiditem_id ? { plaiditem_id } : {}) };

    try {
      const res = await axios.get(`/api/v2/create_link_token/`, {
        headers: { "Content-Type": "application/json" },
        params,
        withCredentials: true,
      });
      setPublicToken(res.data.link_token);
    } catch (err) {
      console.error("Failed to create link token:", err);
      setSnackbarMsg(formatError(err));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setLoading(false);
    setPublicToken(null);
  };

  const openFakePlaidLink = () => {
    setShowFake(true);
  };

  return (
    <>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {plaiditem_id ? (
          <Button
            fullWidth
            variant="outlined"
            onClick={(event) => {
              if (event.shiftKey) {
                openFakePlaidLink();
                return;
              }
              openPlaidLink();
            }}
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SvgIcon>
                  <image href="/left-icon.svg" width="24" height="24" />
                </SvgIcon>
              )
            }
            sx={{
              fontFamily: "Lato",
              textTransform: "none",
              borderColor: "#536DEC",
              color: "#536DEC",
              fontWeight: 600,
              borderRadius: "8px",
              "&:hover": {
                borderColor: "#455FDE",
                backgroundColor: "rgba(83, 109, 236, 0.08)",
              },
              "&:active": {
                borderColor: "#3E55C8",
                backgroundColor: "rgba(83, 109, 236, 0.15)",
              },
              py: 1.2,
            }}
          >
            Relink
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={(event) => {
              if (event.shiftKey) {
                openFakePlaidLink();
                return;
              }
              openPlaidLink();
            }}
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SvgIcon>
                  <image href="/left-icon (1).svg" width="24" height="24" />
                </SvgIcon>
              )
            }
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontFamily: "Lato",
              height: missing ? "48px" : "40px",
              width: missing ? "274px" : "188px",
            }}
          >
            Link bank account
          </Button>
        )}

        {publicToken && (
          <LinkPlaid
            customer_id={customer_id}
            refreshList={refreshList}
            public_token={publicToken}
            close={close}
            refresh={plaiditem_id !== undefined}
            product={product}
            setProfile={setProfile}
          />
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={8000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MuiAlert
            onClose={() => setSnackbarOpen(false)}
            severity="error"
            elevation={6}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMsg}
          </MuiAlert>
        </Snackbar>

        <FakePlaidDialog
          open={showFake}
          onClose={() => setShowFake(false)}
          customer_id={customer_id}
          onSuccess={() => {
            refreshList?.(customer_id);
          }}
        />
      </ThemeProvider>
    </>
  );
};

const LinkPlaid = ({
  customer_id,
  refreshList,
  public_token,
  close,
  refresh,
  product,
  setProfile,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOnSuccess = async (public_token, metadata) => {
    try {
      await axios.post("/api/v2/exchange_public_token/", {
        public_token,
        customer_id,
        refresh,
        product,
      });

      refreshList(customer_id);

      if (setProfile) {
        const currentProfile = await fetchCurrentProfile();
        setProfile(currentProfile[0]);
      }

      close();
      setIsOpen(false);
    } catch (err) {
      console.error("Plaid link error:", err);
    }
  };

  const handleEvent = (eventName, metadata) => {
    if (eventName === "EXIT") {
      close();
      setIsOpen(false);
    }
  };

  const config = {
    token: public_token,
    onSuccess: handleOnSuccess,
    onEvent: handleEvent,
    onExit: () => {},
  };

  const { open, ready } = usePlaidLink(config);

  if (ready && !isOpen) {
    setIsOpen(true);
    open();
  }

  return null;
};
